/**
 * Settings > Extensions > Basecamp > Project folders
 *
 * One default ingest folder for the workspace, plus per-project overrides for the
 * exceptions. To-do-list rules route inside a project and are matched by cached
 * Basecamp list id. Admins/owners write; members read.
 *
 * Bet: docs/product/bets/2026-08-27-basecamp-project-folder-mappings-in-settings.md
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ArrowRight,
  Check,
  ChevronDown,
  FolderTree,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { testIds } from '@/lib/test-ids'
import {
  QUIET_OVERFLOW_MENU_COLLISION_PADDING,
  QUIET_OVERFLOW_MENU_CONTENT_CLASS,
} from '@/lib/menu-chrome'
import { formatUserFacingStoragePath } from '@/lib/app-routes'
import { useWorkspace } from '@/providers/workspace-provider'
import {
  SettingsSection,
} from '@/components/features/settings/settings-section'
import {
  basecampApi,
  type BasecampIngestCatalogProject,
  type BasecampIngestConfig,
  type BasecampIngestFolderRule,
} from '@/lib/basecamp-api'

/** Exclusive accordion: opening one card closes another. Ease-in-out so collapse does not dump height in the first frames. */
const EXPAND_TRANSITION = 'grid-template-rows 520ms ease-in-out'

interface OverrideDraft {
  projectId: string
  projectName: string
  folder: string
  rules: BasecampIngestFolderRule[]
}

interface BasecampProjectFolderMappingsCardProps {
  organizationId: string
  /** Admin/owner. Members see the same data read-only. */
  canEdit: boolean
}

/** Copy a saved override into editable form so drafts never alias loaded state. */
function toDraft(
  projectId: string,
  projectName: string,
  folder: string,
  rules: BasecampIngestFolderRule[],
): OverrideDraft {
  return { projectId, projectName, folder, rules: rules.map((r) => ({ ...r })) }
}

/** Settings card for the workspace default ingest folder and its per-project exceptions. */
export function BasecampProjectFolderMappingsCard({
  organizationId,
  canEdit,
}: BasecampProjectFolderMappingsCardProps) {
  const { workspaces } = useWorkspace()
  const [config, setConfig] = useState<BasecampIngestConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [defaultFolder, setDefaultFolder] = useState('')
  const [overrides, setOverrides] = useState<OverrideDraft[]>([])
  const [removedOverrides, setRemovedOverrides] = useState<OverrideDraft[]>([])
  const [expandedProjectId, setExpandedProjectId] = useState<string | null>(null)
  const [addFilter, setAddFilter] = useState('')
  const [addOpen, setAddOpen] = useState(false)

  const applyConfig = useCallback((next: BasecampIngestConfig) => {
    setConfig(next)
    setDefaultFolder(next.default_folder || next.suggested_default_folder || '')
    setOverrides(
      (next.overrides || []).map((o) =>
        toDraft(o.projectId, o.projectName || o.projectId, o.folder || '', o.rules || []),
      ),
    )
    setRemovedOverrides([])
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const next = await basecampApi.getIngestConfig(organizationId)
      applyConfig(next)
    } catch (err) {
      console.error('[ProjectFolders]', err)
      toast.error(err instanceof Error ? err.message : 'Could not load project folders')
    } finally {
      setLoading(false)
    }
  }, [organizationId, applyConfig])

  useEffect(() => {
    void load()
  }, [load])

  const catalog = config?.catalog || []
  const catalogById = useMemo(() => {
    const map = new Map<string, BasecampIngestCatalogProject>()
    for (const project of catalog) map.set(project.projectId, project)
    return map
  }, [catalog])

  const overrideIds = useMemo(() => new Set(overrides.map((o) => o.projectId)), [overrides])

  const addableProjects = useMemo(() => {
    const needle = addFilter.trim().toLowerCase()
    return catalog
      .filter((p) => !overrideIds.has(p.projectId))
      .filter(
        (p) =>
          !needle ||
          p.name.toLowerCase().includes(needle) ||
          p.projectId.includes(needle),
      )
  }, [catalog, overrideIds, addFilter])

  const isDirty = useMemo(() => {
    if (!config) return false
    if (removedOverrides.length > 0) return true
    if ((defaultFolder || '') !== (config.default_folder || '')) return true
    if (config.default_folder_is_suggested && defaultFolder) return true
    if (overrides.length !== (config.overrides || []).length) return true
    return overrides.some((draft) => {
      const saved = (config.overrides || []).find((o) => o.projectId === draft.projectId)
      if (!saved) return true
      if ((saved.folder || '') !== draft.folder) return true
      return JSON.stringify(saved.rules || []) !== JSON.stringify(draft.rules)
    })
  }, [config, defaultFolder, overrides, removedOverrides])

  const exampleDestination = useMemo(() => {
    const base = (defaultFolder || '').replace(/^\/+|\/+$/g, '')
    if (!base) return ''
    return formatUserFacingStoragePath(`${base}/project-name/todos`, workspaces)
  }, [defaultFolder, workspaces])

  const updateOverride = (projectId: string, patch: Partial<OverrideDraft>) => {
    setOverrides((prev) =>
      prev.map((o) => (o.projectId === projectId ? { ...o, ...patch } : o)),
    )
  }

  const addOverride = (project: BasecampIngestCatalogProject) => {
    setRemovedOverrides((prev) => prev.filter((o) => o.projectId !== project.projectId))
    setOverrides((prev) => [
      ...prev,
      toDraft(
        project.projectId,
        project.name,
        project.folder || `${defaultFolder.replace(/\/+$/, '')}/${slugify(project.name)}`,
        project.rules || [],
      ),
    ])
    setExpandedProjectId(project.projectId)
    setAddOpen(false)
    setAddFilter('')
  }

  const restoreOverride = (draft: OverrideDraft) => {
    setRemovedOverrides((prev) => prev.filter((o) => o.projectId !== draft.projectId))
    setOverrides((prev) =>
      prev.some((o) => o.projectId === draft.projectId) ? prev : [...prev, draft],
    )
    setExpandedProjectId(draft.projectId)
  }

  const confirmRemoveOverride = (draft: OverrideDraft) => {
    const snapshot = toDraft(draft.projectId, draft.projectName, draft.folder, draft.rules)
    setOverrides((prev) => prev.filter((o) => o.projectId !== draft.projectId))
    setRemovedOverrides((prev) =>
      prev.some((o) => o.projectId === draft.projectId) ? prev : [...prev, snapshot],
    )
    setExpandedProjectId((prev) => (prev === draft.projectId ? null : prev))
    toast.message(`Removed override for ${draft.projectName}`, {
      description: 'Save to persist. Undo restores this row.',
      duration: 10000,
      action: {
        label: 'Undo',
        onClick: () => restoreOverride(snapshot),
      },
    })
  }

  const addRule = (projectId: string) => {
    const lists = catalogById.get(projectId)?.lists || []
    const first = lists[0]
    updateOverride(projectId, {
      rules: [
        ...(overrides.find((o) => o.projectId === projectId)?.rules || []),
        {
          match: 'list_id',
          listId: first ? first.id : '',
          value: first ? first.title : '',
          folder: '',
        },
      ],
    })
  }

  const updateRule = (
    projectId: string,
    index: number,
    patch: Partial<BasecampIngestFolderRule>,
  ) => {
    const current = overrides.find((o) => o.projectId === projectId)
    if (!current) return
    updateOverride(projectId, {
      rules: current.rules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)),
    })
  }

  const removeRule = (projectId: string, index: number) => {
    const current = overrides.find((o) => o.projectId === projectId)
    if (!current) return
    updateOverride(projectId, { rules: current.rules.filter((_, i) => i !== index) })
  }

  const save = async () => {
    if (!defaultFolder.trim()) {
      toast.error('Set a default ingest folder first')
      return
    }
    setSaving(true)
    try {
      const payload = {
        default_folder: defaultFolder.trim(),
        overrides: [
          ...overrides.map((o) => ({
            projectId: o.projectId,
            folder: o.folder.trim(),
            // Only complete rules are worth persisting
            rules: o.rules.filter((r) => r.listId && r.folder.trim()),
          })),
          ...removedOverrides.map((o) => ({
            projectId: o.projectId,
            folder: '',
            rules: [] as BasecampIngestFolderRule[],
          })),
        ],
      }
      await basecampApi.updateIngestConfig(payload, organizationId)
      toast.success('Project folders saved')
      await load()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save project folders')
    } finally {
      setSaving(false)
    }
  }

  return (
    <SettingsSection
      data-testid={testIds.workspaceSettings.basecampProjectMappingsPanel}
      icon={<FolderTree />}
      title="Project folders"
      badge={
        overrides.length > 0 ? (
          <Badge variant="outline" className="text-xs font-medium text-muted-foreground">
            {overrides.length} override{overrides.length === 1 ? '' : 's'}
          </Badge>
        ) : null
      }
      description="Where Basecamp work lands in Maater. Everything ingests under the default folder unless a project override says otherwise. Renaming the default retargets new ingest only - existing documents stay put."
      actions={
        <Button
          size="sm"
          variant="ghost"
          className="shrink-0 -mr-1"
          onClick={() => void load()}
          disabled={loading || saving}
          aria-label="Reload project folders"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
        </Button>
      }
    >
      {loading && !config ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading project folders...
        </div>
      ) : (
        <>
          <div className="space-y-2">
            <label
              className="text-xs font-medium text-muted-foreground"
              htmlFor="basecamp-default-ingest-folder"
            >
              Default ingest folder
            </label>
            <Input
              id="basecamp-default-ingest-folder"
              value={defaultFolder}
              onChange={(e) => setDefaultFolder(e.target.value)}
              placeholder={config?.suggested_default_folder || 'workspace/basecamp'}
              className="h-9 font-mono text-xs"
              disabled={!canEdit || saving}
              data-testid={testIds.workspaceSettings.basecampProjectMappingsDefaultInput}
            />
            {exampleDestination ? (
              <p className="text-[11px] text-muted-foreground">
                A project with no override lands in{' '}
                <span className="font-medium text-foreground">{exampleDestination}</span>
              </p>
            ) : null}
            {config?.default_folder_is_suggested ? (
              <p className="text-[11px] text-muted-foreground">
                Suggested from this workspace slug. Save to make it the default.
              </p>
            ) : null}
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-muted-foreground">
                Overrides
                <span className="ml-1 font-normal">(exceptions only)</span>
              </span>
              {canEdit ? (
                <AddProjectMenu
                  open={addOpen}
                  onOpenChange={(next) => {
                    setAddOpen(next)
                    if (!next) setAddFilter('')
                  }}
                  filter={addFilter}
                  onFilterChange={setAddFilter}
                  projects={addableProjects}
                  totalAddable={catalog.filter((p) => !overrideIds.has(p.projectId)).length}
                  onSelect={addOverride}
                />
              ) : null}
            </div>

            {overrides.length === 0 ? (
              <p className="rounded-md border border-dashed border-border px-3 py-6 text-center text-xs text-muted-foreground">
                No overrides. Every Basecamp project ingests under the default folder.
              </p>
            ) : (
              <ul className="space-y-2.5">
                {overrides.map((override) => {
                  const project = catalogById.get(override.projectId)
                  const lists = project?.lists || []
                  const isExpanded = expandedProjectId === override.projectId
                  return (
                    <li
                      key={override.projectId}
                      className="rounded-md border border-border bg-background/60 px-3.5 py-3"
                      data-testid={testIds.workspaceSettings.basecampProjectMappingsOverride(
                        override.projectId,
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          className="flex min-w-0 flex-1 items-center gap-2 text-left text-sm font-medium"
                          onClick={() =>
                            setExpandedProjectId(isExpanded ? null : override.projectId)
                          }
                          data-testid={testIds.workspaceSettings.basecampProjectMappingsOverrideToggle(
                            override.projectId,
                          )}
                        >
                          <ChevronDown
                            className={cn(
                              'h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-500 ease-in-out',
                              isExpanded ? 'rotate-0' : '-rotate-90',
                            )}
                          />
                          <span className="truncate">{override.projectName}</span>
                          {override.rules.length > 0 ? (
                            <Badge
                              variant="outline"
                              className="shrink-0 text-[10px] font-medium text-muted-foreground"
                            >
                              {override.rules.length} list rule
                              {override.rules.length === 1 ? '' : 's'}
                            </Badge>
                          ) : null}
                        </button>
                      </div>

                      <div
                        className="grid"
                        style={{
                          gridTemplateRows: isExpanded ? '1fr' : '0fr',
                          transition: EXPAND_TRANSITION,
                        }}
                      >
                        <div className="overflow-hidden">
                          <div className="space-y-3 pt-3">
                            <div className="space-y-1.5">
                              <span className="text-[11px] font-medium text-muted-foreground">
                                Project folder
                              </span>
                              <Input
                                value={override.folder}
                                onChange={(e) =>
                                  updateOverride(override.projectId, { folder: e.target.value })
                                }
                                placeholder={defaultFolder}
                                className="h-8 font-mono text-xs"
                                disabled={!canEdit || saving}
                                data-testid={testIds.workspaceSettings.basecampProjectMappingsOverrideFolder(
                                  override.projectId,
                                )}
                              />
                            </div>

                            <div className="space-y-2">
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-[11px] font-medium text-muted-foreground">
                                  To-do list rules
                                </span>
                                {canEdit ? (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 gap-1 text-[11px]"
                                    disabled={lists.length === 0 || saving}
                                    onClick={() => addRule(override.projectId)}
                                    data-testid={testIds.workspaceSettings.basecampProjectMappingsRuleAdd(
                                      override.projectId,
                                    )}
                                  >
                                    <Plus className="h-3 w-3" /> Add rule
                                  </Button>
                                ) : null}
                              </div>

                              {lists.length === 0 ? (
                                <p className="text-[11px] text-muted-foreground">
                                  No cached to-do lists for this project yet. Open the project
                                  once in Maater, then add a rule.
                                </p>
                              ) : null}

                              {override.rules.map((rule, index) => (
                                <div
                                  key={`${override.projectId}-${index}`}
                                  className="flex items-center gap-2"
                                >
                                  <ListPicker
                                    lists={lists}
                                    value={rule.listId || ''}
                                    label={rule.value || ''}
                                    disabled={!canEdit || saving}
                                    testId={testIds.workspaceSettings.basecampProjectMappingsRuleList(
                                      override.projectId,
                                      index,
                                    )}
                                    onChange={(listId, title) =>
                                      updateRule(override.projectId, index, {
                                        match: 'list_id',
                                        listId,
                                        value: title,
                                      })
                                    }
                                  />
                                  <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                  <Input
                                    value={rule.folder}
                                    onChange={(e) =>
                                      updateRule(override.projectId, index, {
                                        folder: e.target.value,
                                      })
                                    }
                                    placeholder={override.folder || defaultFolder}
                                    className="h-8 flex-1 font-mono text-xs"
                                    disabled={!canEdit || saving}
                                    data-testid={testIds.workspaceSettings.basecampProjectMappingsRuleFolder(
                                      override.projectId,
                                      index,
                                    )}
                                  />
                                  {canEdit ? (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 w-7 shrink-0 p-0 text-muted-foreground"
                                      onClick={() => removeRule(override.projectId, index)}
                                      aria-label="Remove rule"
                                      data-testid={testIds.workspaceSettings.basecampProjectMappingsRuleRemove(
                                        override.projectId,
                                        index,
                                      )}
                                    >
                                      <X className="h-3.5 w-3.5" />
                                    </Button>
                                  ) : null}
                                </div>
                              ))}
                            </div>

                            {canEdit ? (
                              <OverrideRemoveControls
                                projectId={override.projectId}
                                projectName={override.projectName}
                                defaultFolder={defaultFolder}
                                onConfirm={() => confirmRemoveOverride(override)}
                              />
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
            {canEdit && removedOverrides.length > 0 ? (
              <ul className="space-y-1.5">
                {removedOverrides.map((draft) => (
                  <li
                    key={draft.projectId}
                    className="flex items-center justify-between gap-2 rounded-md border border-dashed border-border px-3 py-2"
                    data-testid={testIds.workspaceSettings.basecampProjectMappingsOverrideUndoRow(
                      draft.projectId,
                    )}
                  >
                    <p className="min-w-0 text-[11px] text-muted-foreground">
                      Removed{' '}
                      <span className="font-medium text-foreground">{draft.projectName}</span>
                      . New ingest uses the default folder until you Save.
                    </p>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-7 shrink-0 text-[11px]"
                      onClick={() => restoreOverride(draft)}
                      data-testid={testIds.workspaceSettings.basecampProjectMappingsOverrideUndo(
                        draft.projectId,
                      )}
                    >
                      Undo
                    </Button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>

          {canEdit ? (
            <div className="flex items-center justify-end gap-2">
              <Button
                size="sm"
                disabled={!isDirty || saving}
                onClick={() => void save()}
                data-testid={testIds.workspaceSettings.basecampProjectMappingsSave}
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
              </Button>
            </div>
          ) : (
            <p
              className="text-[11px] italic text-muted-foreground"
              data-testid={testIds.workspaceSettings.basecampProjectMappingsReadOnlyNote}
            >
              Ask a workspace admin to change where Basecamp work lands.
            </p>
          )}
        </>
      )}
    </SettingsSection>
  )
}

/** Confirmed remove lives in the expanded panel so a header X cannot wipe an override. */
function OverrideRemoveControls({
  projectId,
  projectName,
  defaultFolder,
  onConfirm,
}: {
  projectId: string
  projectName: string
  defaultFolder: string
  onConfirm: () => void
}) {
  const fallback = defaultFolder.replace(/^\/+|\/+$/g, '')
  const unmappedHint = fallback
    ? `${fallback}/{project-name}`
    : 'the default folder / {project-name}'

  return (
    <div className="space-y-2 border-t border-border pt-3">
      <p className="text-[11px] leading-relaxed text-muted-foreground">
        Removing this override does not delete documents. After Save, new ingest for{' '}
        {projectName} uses {unmappedHint} unless you add it back. List rules on this row go
        with it.
      </p>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 text-[11px] text-muted-foreground"
            data-testid={testIds.workspaceSettings.basecampProjectMappingsOverrideRemove(projectId)}
          >
            Remove override
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent
          data-testid={testIds.workspaceSettings.basecampProjectMappingsOverrideRemoveDialog(
            projectId,
          )}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {projectName} override?</AlertDialogTitle>
            <AlertDialogDescription>
              Existing documents stay where they are. New Basecamp ingest for this project
              will follow the workspace default until you add an override again. This stays
              a draft until you Save.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep override</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={onConfirm}
              data-testid={testIds.workspaceSettings.basecampProjectMappingsOverrideRemoveConfirm(
                projectId,
              )}
            >
              Remove override
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

/** Folder segment suggested for a newly added project override. */
function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/** Compact filtered dropdown of cached projects that do not have an override yet. */
function AddProjectMenu({
  open,
  onOpenChange,
  filter,
  onFilterChange,
  projects,
  totalAddable,
  onSelect,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  filter: string
  onFilterChange: (value: string) => void
  projects: BasecampIngestCatalogProject[]
  totalAddable: number
  onSelect: (project: BasecampIngestCatalogProject) => void
}) {
  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 gap-1.5 text-xs font-normal"
          data-testid={testIds.workspaceSettings.basecampProjectMappingsAddTrigger}
        >
          <Plus className="h-3.5 w-3.5" />
          Add project
          {totalAddable > 0 ? (
            <span className="text-muted-foreground">({totalAddable})</span>
          ) : null}
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className={cn(QUIET_OVERFLOW_MENU_CONTENT_CLASS, 'w-72 p-0')}
        collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div
          className="flex items-center gap-2 border-b border-border px-2 py-1.5"
          onKeyDown={(e) => e.stopPropagation()}
        >
          <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <input
            value={filter}
            onChange={(e) => onFilterChange(e.target.value)}
            placeholder="Filter projects..."
            className="h-7 min-w-0 flex-1 bg-transparent text-xs outline-none placeholder:text-muted-foreground/50"
            data-testid={testIds.workspaceSettings.basecampProjectMappingsAddFilter}
            autoFocus
          />
        </div>
        <div className="max-h-60 overflow-y-auto p-1">
          {projects.map((project) => (
            <DropdownMenuItem
              key={project.projectId}
              className="gap-2 text-xs"
              onSelect={() => onSelect(project)}
            >
              <span className="truncate">{project.name}</span>
              {project.lists.length > 0 ? (
                <span className="ml-auto shrink-0 text-[10px] text-muted-foreground">
                  {project.lists.length} lists
                </span>
              ) : null}
            </DropdownMenuItem>
          ))}
          {totalAddable === 0 ? (
            <p className="px-2 py-3 text-xs text-muted-foreground">
              No cached Basecamp projects left to override.
            </p>
          ) : projects.length === 0 ? (
            <p className="px-2 py-3 text-xs text-muted-foreground">No matching projects</p>
          ) : null}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** Picks the cached to-do list a rule matches on, so the UI only ever writes a `list_id`. */
function ListPicker({
  lists,
  value,
  label,
  disabled,
  testId,
  onChange,
}: {
  lists: { id: string; title: string; parentTitle?: string | null }[]
  value: string
  label: string
  disabled?: boolean
  testId: string
  onChange: (listId: string, title: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return lists
    return lists.filter(
      (l) => l.title.toLowerCase().includes(needle) || l.id.includes(needle),
    )
  }, [lists, query])

  const selected = lists.find((l) => l.id === value)
  const triggerLabel = selected?.title || label || 'Select to-do list'

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) setQuery('')
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={disabled}
          className="h-8 max-w-[180px] flex-1 justify-between gap-2 text-xs font-normal"
          data-testid={testId}
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className={cn(QUIET_OVERFLOW_MENU_CONTENT_CLASS, 'w-72 p-0')}
        collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div
          className="flex items-center gap-2 border-b border-border px-2 py-1.5"
          onKeyDown={(e) => e.stopPropagation()}
        >
          <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter lists..."
            className="h-7 min-w-0 flex-1 bg-transparent text-xs outline-none placeholder:text-muted-foreground/50"
            autoFocus
          />
        </div>
        <div className="max-h-60 overflow-y-auto p-1">
          {filtered.map((list) => (
            <DropdownMenuItem
              key={list.id}
              className="gap-2 text-xs"
              onSelect={() => onChange(list.id, list.title)}
            >
              <Check
                className={cn(
                  'h-3.5 w-3.5 shrink-0',
                  value === list.id ? 'opacity-100' : 'opacity-0',
                )}
              />
              <span className="truncate">{list.title}</span>
              {list.parentTitle ? (
                <span className="ml-auto shrink-0 text-[10px] text-muted-foreground">
                  {list.parentTitle}
                </span>
              ) : null}
            </DropdownMenuItem>
          ))}
          {filtered.length === 0 ? (
            <p className="px-2 py-3 text-xs text-muted-foreground">No matching lists</p>
          ) : null}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
